import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import { DOMAttributes, DOMProps } from '@react-types/shared';

import { Grid } from './Grid';
import {
  SapphireViewStyleProps,
  viewStylePropHandlers,
} from '../../View/src/View';
import {
  useResolveResponsiveValue,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { tokens } from '@danske/sapphire-css/themes';

type PageGridStyleProps = Pick<
  SapphireViewStyleProps,
  'backgroundColor' | 'marginTop' | 'marginBottom'
>;

export interface PageGridProps
  extends DOMProps,
    PageGridStyleProps,
    Omit<
      DOMAttributes,
      'children' | 'style' | 'className' | 'dangerouslySetInnerHTML'
    > {
  children: ReactNode;
}

export const PageGrid = forwardRef(function PageGrid(
  props: PageGridProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { children, ...otherProps } = props;
  const { styleProps, filteredProps } = useSapphireStyleProps<
    Omit<PageGridProps, 'children'>,
    PageGridStyleProps
  >(otherProps, viewStylePropHandlers);
  const resolveResponsiveValue = useResolveResponsiveValue();

  const style = {
    ...styleProps.style,
    padding: resolveResponsiveValue({
      base: `0px ${tokens.size.spacingMd.value}`,
      md: `0px ${tokens.size.spacingLg.value}`,
    }),
  };

  return (
    <div
      {...filteredProps}
      style={style}
      ref={ref}
      className={styleProps.className}
    >
      <Grid
        gap={{
          base: tokens.size.spacing2xs.value,
          md: tokens.size.spacingSm.value,
        }}
        gridTemplateColumns={{
          base: 'repeat(6, 1fr)',
          sm: 'repeat(12, 1fr)',
        }}
        maxWidth={'var(--sapphire-global-size-generic-3200)'}
        margin={'0 auto'}
      >
        {children}
      </Grid>
    </div>
  );
});
